import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CpfSharedService {
  // CPFs separados por pessoa
  private lastRecipientCpfSource = new BehaviorSubject<string>('');
  private lastApplicantCpfSource = new BehaviorSubject<string>('');

  // CPF geral (para compatibilidade)
  private currentCpfSource = new BehaviorSubject<string>('');
  public currentCpf = this.currentCpfSource.asObservable();

  // Observables públicos
  public lastRecipientCpf$ = this.lastRecipientCpfSource.asObservable();
  public lastApplicantCpf$ = this.lastApplicantCpfSource.asObservable();

  // Estado da pessoa atual
  private currentPersonSubject = new BehaviorSubject<'recipient' | 'applicant'>(
    'recipient'
  );
  public currentPerson$ = this.currentPersonSubject.asObservable();

  // Para cadastro (mantendo compatibilidade)
  private cpfCadastradoSource = new BehaviorSubject<string>('');
  public cpfCadastrado$ = this.cpfCadastradoSource.asObservable();

  // TOGGLE FUNCTIONALITY
  private toggleSource = new BehaviorSubject<boolean>(true);
  public currentToggle = this.toggleSource.asObservable();

  // Métodos para CPF do beneficiário
  setRecipientCpf(cpf: string): void {
    this.lastRecipientCpfSource.next(cpf);
  }

  getRecipientCpf(): string {
    return this.lastRecipientCpfSource.value;
  }

  clearRecipientCpf(): void {
    this.lastRecipientCpfSource.next('');
  }

  // Métodos para CPF do solicitante
  setApplicantCpf(cpf: string): void {
    this.lastApplicantCpfSource.next(cpf);
  }

  getApplicantCpf(): string {
    return this.lastApplicantCpfSource.value;
  }

  clearApplicantCpf(): void {
    this.lastApplicantCpfSource.next('');
  }

  // Métodos para pessoa atual
  setCurrentPerson(person: 'recipient' | 'applicant'): void {
    this.currentPersonSubject.next(person);
  }

  getCurrentPerson(): 'recipient' | 'applicant' {
    return this.currentPersonSubject.value;
  }

  // Métodos para cadastro (mantidos para compatibilidade)
  setCpfCadastrado(cpf: string): void {
    this.cpfCadastradoSource.next(cpf);
  }

  // Métodos para o CPF geral (currentCpf)
  setCpf(cpf: string): void {
    this.currentCpfSource.next(cpf);
  }

  clearCpf(): void {
    this.currentCpfSource.next('');
  }

  getCurrentCpf(): Observable<string> {
    return this.currentCpfSource.asObservable();
  }

  getCurrentCpfValue(): string {
    return this.currentCpfSource.value;
  }

  // Método para obter CPF baseado na pessoa atual (para compatibilidade)
  getCpfForCurrentPerson(): string {
    const currentPerson = this.getCurrentPerson();
    return currentPerson === 'recipient'
      ? this.getRecipientCpf()
      : this.getApplicantCpf();
  }

  // MÉTODOS DO TOGGLE
  setToggleValue(value: boolean): void {
    // console.log('CpfSharedService: setToggleValue chamado com', value);
    this.toggleSource.next(value);
  }

  getToggleValue(): boolean {
    return this.toggleSource.value;
  }

  // MÉTODOS ADICIONAIS PARA AMBOS OS CPFs - ADICIONE ESTES
  getBothCpfs(): { recipientCpf: string; applicantCpf: string } {
    return {
      recipientCpf: this.getRecipientCpf(),
      applicantCpf: this.getApplicantCpf()
    };
  }

  clearBothCpfs(): void {
    this.clearRecipientCpf();
    this.clearApplicantCpf();
    this.clearCpf();
  }

  // Método adicional para verificar se ambos os CPFs estão preenchidos
  hasBothCpfs(): boolean {
    return !!this.getRecipientCpf() && !!this.getApplicantCpf();
  }

  // Método para limpar todos os dados
  clearAll(): void {
    this.clearBothCpfs();
    this.setToggleValue(true);
    this.setCurrentPerson('recipient');
    this.setCpfCadastrado('');
  }
}